import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  UncontrolledTooltip,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import FilterPanel from "Components/Common/FilterPanel";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";

import { fetchTransactionReportThunk } from "slices/Report/thunks";
import { exportToExcel } from "helpers/exportToExcel";
import { showSuccessToast, showErrorToast } from "helpers/toast_helper";
import { listAllTransactionReport } from "services/reportService";

import TransactionContainer from "./transactionContainer";
import moment from "moment";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";

const selectReportState = (state: RootState) => (state as any).report;

const reportSelector = createSelector(selectReportState, (report) => ({
  transactionList: report.transactionList,
  loading: report.loading,
  pagination: report.pagination,
}));

const TransactionPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();

  const { transactionList, loading, pagination } = useSelector(reportSelector);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [paymentMethodName, setPaymentMethodName] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [transactionTypeFilter, setTransactionTypeFilter] = useState("");
  const [frequency, setFrequency] = useState("");
  const [accountName, setAccountName] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [transactionType, setTransactionType] = useState<string>("Financial");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [transactionTypeDropdownOpen, setTransactionTypeDropdownOpen] = useState(false);

  const rawTransactionData = transactionList?.transactions || [];

  const transactionData = useMemo(() => {
    if (!transactionTypeFilter) return rawTransactionData;
    return rawTransactionData.filter((t: any) => t.transactiontype === transactionTypeFilter);
  }, [rawTransactionData, transactionTypeFilter]);

  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(
        fetchTransactionReportThunk({
          from_date: fromDate,
          to_date: toDate,
          paymentmethodname: paymentMethodName,
          category_name: categoryName,
          frequency: frequency,
          searchkey: searchQuery,
          accountname: accountName,
          phone_no: phoneNo,
          transaction_type: transactionType,
          page: page,
          page_size: limit,
        })
      );
    }, 500);

    return () => clearTimeout(handler);
  }, [
    dispatch,
    page,
    limit,
    searchQuery,
    fromDate,
    toDate,
    paymentMethodName,
    categoryName,
    frequency,
    accountName,
    phoneNo,
    transactionType,
  ]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, fromDate, toDate, paymentMethodName, categoryName, transactionTypeFilter, frequency, accountName, phoneNo, transactionType]);

  const filterOptions = useMemo(() => {
    const categorySet = new Set<string>();
    const paymentMethodSet = new Set<string>();
    const transactionTypeSet = new Set<string>();

    rawTransactionData.forEach((t: any) => {
      if (t.category_name) categorySet.add(t.category_name);
      if (t.paymentmethodname) paymentMethodSet.add(t.paymentmethodname);
      if (t.transactiontype) transactionTypeSet.add(t.transactiontype);
    });

    return {
      categories: Array.from(categorySet).sort().map((cat) => ({ label: cat, value: cat })),
      paymentMethods: Array.from(paymentMethodSet).sort().map((pm) => ({ label: pm, value: pm })),
      transactionTypes: Array.from(transactionTypeSet).sort().map((tt) => ({ label: tt, value: tt })),
    };
  }, [rawTransactionData]);

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowClick = useCallback((row: any) => {
  }, []);

  const handleSearch = () => {
    setPage(1);
    dispatch(
      fetchTransactionReportThunk({
        from_date: fromDate,
        to_date: toDate,
        paymentmethodname: paymentMethodName,
        category_name: categoryName,
        frequency: frequency,
        searchkey: searchQuery,
        accountname: accountName,
        phone_no: phoneNo,
        transaction_type: transactionType || undefined,
        page: 1,
        page_size: limit,
      })
    );
  };

  const handleApplyFilters = () => {
    setPage(1);
    setIsFilterPanelOpen(false);
    dispatch(
      fetchTransactionReportThunk({
        from_date: fromDate,
        to_date: toDate,
        paymentmethodname: paymentMethodName,
        category_name: categoryName,
        frequency: frequency,
        searchkey: searchQuery,
        accountname: accountName,
        phone_no: phoneNo,
        transaction_type: transactionType || undefined,
        page: 1,
        page_size: limit,
      })
    );
  };

  const handleClearFilters = () => {
    setCategoryName("");
    setPaymentMethodName("");
    setTransactionTypeFilter("");
    setTransactionType("Financial");
    setPage(1);
  };

  const handleDownload = useCallback(async () => {
    try {
      if (!pagination?.total_pages) {
        showErrorToast("Pagination data missing!");
        return;
      }

      showSuccessToast("Preparing full export...");

      let allTransactions: any[] = [];

      for (let p = 1; p <= pagination.total_pages; p++) {
        const response = await listAllTransactionReport({
          from_date: fromDate,
          to_date: toDate,
          paymentmethodname: paymentMethodName,
          category_name: categoryName,
          frequency: frequency,
          searchkey: searchQuery,
          accountname: accountName,
          phone_no: phoneNo,
          transaction_type: transactionType,
          page: p,
          page_size: limit, // Use current page size
        });

        if (response?.success && response?.data?.transactions) {
          allTransactions = [...allTransactions, ...response.data.transactions];
        }
      }

      if (allTransactions.length === 0) {
        showErrorToast("No data available to export!");
        return;
      }

      exportToExcel(allTransactions, "Transaction_Report");
      showSuccessToast("Transaction report exported successfully!");
    } catch (error: any) {
      const message =
        error?.message ||
        error?.response?.data?.message ||
        "Failed to export transactions";
      showErrorToast(message);
    }
  }, [pagination, limit, fromDate, toDate, paymentMethodName, categoryName, frequency, searchQuery, accountName, phoneNo, transactionType]);

  const handleFilter = () => {
    setIsFilterPanelOpen(true);
  };

  const formatDate = (d: string) => {
    if (!d) return "-";
    return moment(d).format("DD MMM YYYY");
  };

  const formatDateTime = (d: string) => {
    if (!d) return "-";
    return moment(d).format("DD MMM YYYY HH:mm");
  };

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      { header: "Username", accessorKey: "username" },
      { header: "Category name", accessorKey: "category_name" },
      {
        header: "Amount",
        accessorKey: "amount",
        cell: (cell: any) => {
          const amount = cell.getValue();
          const symbol = cell.row.original.currencysymbol || "";
          return `${symbol} ${amount?.toFixed(2) || "0.00"}`;
        },
      },
      { header: "Payment method", accessorKey: "paymentmethodname" },
      { header: "Phone no.", accessorKey: "phone_no" },
      { header: "Account name", accessorKey: "accountname" },
      {
        header: "Created at",
        accessorKey: "created_at",
        cell: (cell: any) => formatDate(cell.getValue()),
      },
      {
        header: "Updated at",
        accessorKey: "updated_at",
        cell: (cell: any) => formatDate(cell.getValue()),
      },
      { header: "Currency", accessorKey: "currencycode" },
      { header: "Transaction type", accessorKey: "transactiontype" },
      {
        header: "Payment Sub Option",
        accessorKey: "paymentsuboption",
        cell: (cell: any) => {
          const value = cell.getValue();
          return value && String(value).trim() ? value : "-";
        },
      },
      {
        header: "Frequency",
        accessorKey: "frequency",
        cell: (cell: any) => {
          const value = cell.getValue();
          return value && String(value).trim() ? value : "-";
        },
      },
      {
        header: "Date & Time",
        accessorKey: "txn_datetime",
        cell: (cell: any) => formatDateTime(cell.getValue()),
      },
      { header: "Transaction option", accessorKey: "transactionoption" },
    ],
    []
  );

  document.title = "Transaction Report";

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Transaction Report" pageTitle={["Report", "Transaction Report"]} />

        <Row className="align-items-center g-3 pb-4 pe-3">
        <Col md={3}>
            <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search Transactions..."
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
              <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
          </Col>

          <Col md={9} className="d-flex justify-content-end gap-2 align-items-center">
            <Button
              id="download-btn-transaction"
              color="primary"
              outline
              type="button"
              onClick={handleDownload}
              size="sm"
              style={{ minWidth: "40px", padding: "0.375rem 0.5rem" }}
            >
              <i className="ri-download-line" style={{ fontSize: "18px" }}></i>
            </Button>
            <UncontrolledTooltip 
              placement="top" 
              target="download-btn-transaction"
            >
              <span style={{ color: 'white' }}>Download</span>
            </UncontrolledTooltip>
            <Button
              id="filter-btn-transaction"
              color="secondary"
              outline
              type="button"
              onClick={handleFilter}
              size="sm"
              style={{ minWidth: "40px", padding: "0.375rem 0.5rem" }}
            >
              <i className="ri-filter-line" style={{ fontSize: "18px" }}></i>
            </Button>
            <UncontrolledTooltip 
              placement="top" 
              target="filter-btn-transaction"
            >
              <span style={{ color: 'white' }}>Filter</span>
            </UncontrolledTooltip>
            <Dropdown isOpen={transactionTypeDropdownOpen} toggle={() => setTransactionTypeDropdownOpen(!transactionTypeDropdownOpen)}>
              <DropdownToggle color="primary" outline>
                {transactionType}
                <i className="ri-arrow-down-s-line ms-2"></i>
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem
                  onClick={() => {
                    setTransactionType("Financial");
                    setTransactionTypeDropdownOpen(false);
                  }}
                  active={transactionType === "Financial"}
                >
                  Financial
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    setTransactionType("Non-Financial");
                    setTransactionTypeDropdownOpen(false);
                  }}
                  active={transactionType === "Non-Financial"}
                >
                  Non-Financial
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card id="transactionList" className="border-0 shadow-none" style={{ border: "none" }}>
              <CardBody className="pt-3" style={{ minHeight: "500px" }}>
                <div className="tab-content">
                  {!loading ? (
                    transactionData?.length > 0 ? (
                      <TransactionContainer
                        columns={columns}
                        data={transactionData}
                        pagination={pagination}
                        limit={limit}
                        setLimit={setLimit}
                        setPage={setPage}
                        onPageChange={(p) => setPage(p)}
                        onRowClick={handleRowClick}
                        tableClass="align-middle border-bottom rounded-pill"
                        theadClass="bg-primary text-white border-top border-success"
                        trClass=""
                        divClass="table-responsive table-card"
                      />
                    ) : (
                      <div className="text-center p-4">No records found.</div>
                    )
                  ) : (
                    <Loader />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <FilterPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        title="Filter Transactions"
        filters={[
          {
            label: "Category Name",
            value: categoryName,
            options: filterOptions.categories,
            onChange: (selected: any) => setCategoryName(selected?.value || ""),
          },
          {
            label: "Payment Method",
            value: paymentMethodName,
            options: filterOptions.paymentMethods,
            onChange: (selected: any) => setPaymentMethodName(selected?.value || ""),
          },
          {
            label: "Transaction Type",
            value: transactionTypeFilter,
            options: filterOptions.transactionTypes,
            onChange: (selected: any) => setTransactionTypeFilter(selected?.value || ""),
          },
        ]}
      />
    </div>
  );
};

export default TransactionPage;
